const UNIT_STORAGE_KEY = 'vm-measure-unit';
const UNIT_EVENT = 'vm-measure:unit';
const DIM_STORAGE_KEY = 'vm-measure-dim';
const DIM_EVENT = 'vm-measure:dim';
const DIMENSIONS = ['width', 'length'];

class MeasurementFilter {
  constructor(root) {
    this.root = root;
    this.endpoint = root.dataset.endpoint || '';
    this.collection = root.dataset.collection || '';
    this.unit = this.readUnit();
    this.dimension = this.readDimension();

    this.status = root.querySelector('[data-measure-status]');
    this.applyButton = root.querySelector('[data-measure-apply]');
    this.clearButton = root.querySelector('[data-measure-clear]');
    this.unitButtons = [...root.querySelectorAll('[data-measure-unit]')];
    this.unitSuffixes = [...root.querySelectorAll('[data-measure-unit-suffix]')];
    this.dimButtons = [...root.querySelectorAll('[data-measure-dim-btn]')];
    this.dimGroups = [...root.querySelectorAll('[data-measure-dim]')];
    this.inputs = {
      widthMin: root.querySelector('[data-measure-input="wmin"]'),
      widthMax: root.querySelector('[data-measure-input="wmax"]'),
      lengthMin: root.querySelector('[data-measure-input="lmin"]'),
      lengthMax: root.querySelector('[data-measure-input="lmax"]'),
    };

    this.results = document.querySelector('[data-measure-results]');
    this.resultsGrid = this.results?.querySelector('[data-measure-results-grid]') || null;
    this.resultsCount = this.results?.querySelector('[data-measure-results-count]') || null;
    this.resultsEmpty = this.results?.querySelector('[data-measure-results-empty]') || null;
    this.nativeGrid = document.getElementById('ResultsList') || document.querySelector('.main-collection-grid');
    this.nativeCounts = [...document.querySelectorAll('.products-count-wrapper')];

    this.bind();
    this.syncUnitUI();
    this.syncDimUI();
  }

  readUnit() {
    try {
      const stored = window.localStorage.getItem(UNIT_STORAGE_KEY);
      if (stored === 'cm' || stored === 'in') return stored;
    } catch (error) {
      /* storage may be unavailable */
    }
    return this.root.dataset.defaultUnit === 'in' ? 'in' : 'cm';
  }

  writeUnit(unit) {
    try {
      window.localStorage.setItem(UNIT_STORAGE_KEY, unit);
    } catch (error) {
      /* ignore */
    }
  }

  readDimension() {
    try {
      const stored = window.localStorage.getItem(DIM_STORAGE_KEY);
      if (stored === 'width' || stored === 'length') return stored;
    } catch (error) {
      /* storage may be unavailable */
    }
    return 'width';
  }

  writeDimension(dimension) {
    try {
      window.localStorage.setItem(DIM_STORAGE_KEY, dimension);
    } catch (error) {
      /* ignore */
    }
  }

  bind() {
    // Keep our inputs isolated from the native facet form so typing/changing
    // values never triggers Shopify's filter navigation.
    ['input', 'change'].forEach((type) => {
      this.root.addEventListener(type, (event) => event.stopPropagation());
    });

    this.unitButtons.forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        this.setUnit(button.dataset.measureUnit, true);
      });
    });
    this.dimButtons.forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        this.setDimension(button.dataset.measureDimBtn, true);
      });
    });
    if (this.applyButton) {
      this.applyButton.addEventListener('click', () => this.apply());
    }
    if (this.clearButton) {
      this.clearButton.addEventListener('click', () => this.clear());
    }
    Object.values(this.inputs).forEach((input) => {
      if (!input) return;
      input.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          this.apply();
        }
      });
    });
    const clearResults = this.results?.querySelector('[data-measure-clear-results]');
    if (clearResults) clearResults.addEventListener('click', () => this.clear());

    // Keep the unit selection in sync across the desktop and drawer instances.
    document.addEventListener(UNIT_EVENT, (event) => {
      const next = event.detail;
      if ((next === 'cm' || next === 'in') && next !== this.unit) {
        this.unit = next;
        this.syncUnitUI();
      }
    });
    document.addEventListener(DIM_EVENT, (event) => {
      const next = event.detail;
      if ((next === 'width' || next === 'length') && next !== this.dimension) {
        this.dimension = next;
        this.syncDimUI();
      }
    });
  }

  setUnit(unit, broadcast = false) {
    if (unit !== 'cm' && unit !== 'in') return;
    this.unit = unit;
    this.writeUnit(unit);
    this.syncUnitUI();
    if (broadcast) {
      document.dispatchEvent(new CustomEvent(UNIT_EVENT, { detail: unit }));
    }
  }

  syncUnitUI() {
    this.unitButtons.forEach((button) => {
      const active = button.dataset.measureUnit === this.unit;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
    this.unitSuffixes.forEach((node) => {
      node.textContent = this.unit;
    });
  }

  setDimension(dimension, broadcast = false) {
    if (dimension !== 'width' && dimension !== 'length') return;
    this.dimension = dimension;
    this.writeDimension(dimension);
    this.syncDimUI();
    if (broadcast) {
      document.dispatchEvent(new CustomEvent(DIM_EVENT, { detail: dimension }));
    }
  }

  syncDimUI() {
    this.dimButtons.forEach((button) => {
      const active = button.dataset.measureDimBtn === this.dimension;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
    // Overlap: only the active dimension's Min/Max inputs are shown; the other
    // dimension's values are retained in the hidden group and still applied.
    this.dimGroups.forEach((group) => {
      group.hidden = group.dataset.measureDim !== this.dimension;
    });
  }

  readBounds() {
    const value = (input) => {
      if (!input) return null;
      const raw = input.value.trim();
      if (!raw) return null;
      const num = Number(raw);
      if (!Number.isFinite(num) || num < 0) return Number.NaN;
      return num;
    };
    return {
      width: { min: value(this.inputs.widthMin), max: value(this.inputs.widthMax) },
      length: { min: value(this.inputs.lengthMin), max: value(this.inputs.lengthMax) },
    };
  }

  validate(bounds) {
    let hasBound = false;
    for (const dimension of DIMENSIONS) {
      const { min, max } = bounds[dimension];
      if (Number.isNaN(min) || Number.isNaN(max)) {
        return { ok: false, message: 'Please enter valid, non-negative numbers.' };
      }
      if (min !== null || max !== null) hasBound = true;
      if (min !== null && max !== null && min > max) {
        return {
          ok: false,
          message: `${dimension === 'width' ? 'Width' : 'Length'} minimum can\u2019t be larger than its maximum.`,
        };
      }
    }
    if (!hasBound) {
      return { ok: false, message: 'Enter at least one measurement to filter by.' };
    }
    return { ok: true };
  }

  buildUrl(bounds) {
    const url = new URL(this.endpoint, window.location.origin);
    url.searchParams.set('unit', this.unit);
    if (this.collection) url.searchParams.set('collection', this.collection);
    const setParam = (key, num) => {
      if (num !== null && !Number.isNaN(num)) url.searchParams.set(key, String(num));
    };
    setParam('wmin', bounds.width.min);
    setParam('wmax', bounds.width.max);
    setParam('lmin', bounds.length.min);
    setParam('lmax', bounds.length.max);
    return url.toString();
  }

  setStatus(message, isError = false) {
    if (!this.status) return;
    this.status.textContent = message || '';
    this.status.classList.toggle('is-error', Boolean(message) && isError);
  }

  async apply() {
    const bounds = this.readBounds();
    const check = this.validate(bounds);
    if (!check.ok) {
      this.setStatus(check.message, true);
      return;
    }

    this.setStatus('');
    if (this.applyButton) {
      this.applyButton.disabled = true;
      this.applyButton.dataset.loading = 'true';
    }

    try {
      const response = await fetch(this.buildUrl(bounds), { credentials: 'omit' });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || payload.ok === false) {
        throw new Error(payload.error || `measurements_${response.status}`);
      }
      this.renderResults(payload, bounds);
    } catch (error) {
      this.setStatus('Measurement filtering is unavailable right now. Please try again soon.', true);
    } finally {
      if (this.applyButton) {
        this.applyButton.disabled = false;
        delete this.applyButton.dataset.loading;
      }
    }
  }

  summaryText(bounds, count) {
    const noun = count === 1 ? 'item' : 'items';
    const parts = [];
    const describe = (label, dimension) => {
      const { min, max } = bounds[dimension];
      if (min !== null && max !== null) parts.push(`${label} ${min}\u2013${max}${this.unit}`);
      else if (min !== null) parts.push(`${label} \u2265 ${min}${this.unit}`);
      else if (max !== null) parts.push(`${label} \u2264 ${max}${this.unit}`);
    };
    describe('width', 'width');
    describe('length', 'length');
    const filterText = parts.length ? ` \u00b7 ${parts.join(', ')}` : '';
    return `${count} ${noun}${filterText}`;
  }

  renderResults(payload, bounds) {
    if (!this.results || !this.resultsGrid) return;
    const items = Array.isArray(payload.items) ? payload.items : [];

    this.resultsGrid.innerHTML = items.map((item) => this.card(item)).join('');
    if (this.resultsCount) {
      let text = this.summaryText(bounds, payload.count || 0);
      if (payload.truncated) text += ` (showing first ${items.length})`;
      this.resultsCount.textContent = text;
    }
    if (this.resultsEmpty) this.resultsEmpty.hidden = items.length > 0;

    this.results.hidden = false;
    if (this.nativeGrid) this.nativeGrid.style.display = 'none';
    this.nativeCounts.forEach((node) => {
      node.dataset.measureHidden = 'true';
      node.style.display = 'none';
    });
    if (this.clearButton) this.clearButton.hidden = false;

    this.setStatus('');
    // Close the open facet dropdown so results are visible.
    const details = this.root.closest('details');
    if (details) details.removeAttribute('open');
    this.results.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  clear() {
    document.querySelectorAll('[data-measurement-filter] [data-measure-input]').forEach((input) => {
      input.value = '';
    });
    if (this.results) {
      this.results.hidden = true;
      if (this.resultsGrid) this.resultsGrid.innerHTML = '';
    }
    if (this.nativeGrid) this.nativeGrid.style.display = '';
    this.nativeCounts.forEach((node) => {
      if (node.dataset.measureHidden) {
        delete node.dataset.measureHidden;
        node.style.display = '';
      }
    });
    document.querySelectorAll('[data-measurement-filter] [data-measure-clear]').forEach((button) => {
      button.hidden = true;
    });
    this.setStatus('');
  }

  card(item) {
    const image = item.image_url
      ? `<img src="${escapeHtml(item.image_url)}" alt="${escapeHtml(item.image_alt || item.title)}" loading="lazy">`
      : '<div class="vm-measure-card__placeholder" aria-hidden="true"></div>';
    const price = item.price ? `<span class="vm-measure-card__price">${escapeHtml(item.price)}</span>` : '';
    const dims = [];
    if (item.width) dims.push(`W ${escapeHtml(item.width)}`);
    if (item.length) dims.push(`L ${escapeHtml(item.length)}`);
    const measure = dims.length
      ? `<span class="vm-measure-card__dims">${dims.join(' \u00b7 ')} ${escapeHtml(this.unit)}</span>`
      : '';
    const soldOut = item.available ? '' : '<span class="vm-measure-card__status">Sold out</span>';

    return `
      <li class="vm-measure-card">
        <a class="vm-measure-card__link" href="${escapeHtml(item.url)}">
          <span class="vm-measure-card__media">${image}${soldOut}</span>
          <span class="vm-measure-card__title">${escapeHtml(item.title)}</span>
          <span class="vm-measure-card__meta">${price}${measure}</span>
        </a>
      </li>
    `;
  }
}

function escapeHtml(value) {
  return String(value === null || value === undefined ? '' : value).replace(/[&<>"']/g, (char) => {
    const entities = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
    return entities[char];
  });
}

function init() {
  document.querySelectorAll('[data-measurement-filter]').forEach((root) => {
    if (root.dataset.measureReady) return;
    root.dataset.measureReady = 'true';
    new MeasurementFilter(root);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
